import { useState, useEffect, useCallback } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus, AuthError } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { apiService } from '../services/api';

/**
 * Auth hata türleri
 */
export type AuthErrorType = 'unauthorized' | 'network' | 'unknown' | null;

/**
 * useAuth Hook
 * MSAL authentication işlemlerini yönetir
 */
export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<AuthErrorType>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Token'ı sessizce al
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      // Silent token alınamadı, popup ile dene
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      } catch (popupError) {
        console.error('Token alınamadı:', popupError);
        return null;
      }
    }
  }, [instance, account]);

  // Auth durumunu kontrol et
  useEffect(() => {
    const checkAuth = async () => {
      if (inProgress === InteractionStatus.None) {
        if (account) {
          const token = await getAccessToken();
          if (token) {
            apiService.setAccessToken(token);
            setIsAuthenticated(true);
            setAuthError(null);
            setErrorMessage(null);
          }
        }
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [account, inProgress, getAccessToken]);

  // Giriş yap
  const login = useCallback(async () => {
    try {
      setAuthError(null);
      setErrorMessage(null);
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Giriş hatası:', error);
      
      // Hata türünü belirle
      if (error instanceof AuthError) {
        const errorCode = error.errorCode;
        const message = error.message;
        
        // AADSTS50105: Kullanıcı uygulamaya atanmamış
        if (errorCode === 'access_denied' || message.includes('AADSTS50105')) {
          setAuthError('unauthorized');
          setErrorMessage('Bu uygulamaya erişim yetkiniz bulunmuyor. Lütfen IT departmanınızla iletişime geçin.');
        }
        // Diğer Azure AD hataları
        else if (message.includes('AADSTS')) {
          setAuthError('unknown');
          setErrorMessage(message);
        }
        // Ağ hataları
        else if (errorCode === 'network_error') {
          setAuthError('network');
          setErrorMessage('Ağ bağlantısı kurulamadı. Lütfen internet bağlantınızı kontrol edin.');
        }
        else {
          setAuthError('unknown');
          setErrorMessage('Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.');
        }
      }
    }
  }, [instance]);

  // Çıkış yap
  const logout = useCallback(async () => {
    try {
      setAuthError(null);
      setErrorMessage(null);
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  }, [instance]);

  // Hata durumunu temizle
  const clearError = useCallback(() => {
    setAuthError(null);
    setErrorMessage(null);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    authError,
    errorMessage,
    user: account ? {
      id: account.localAccountId,
      displayName: account.name || 'Kullanıcı',
      email: account.username,
    } : null,
    login,
    logout,
    getAccessToken,
    clearError,
  };
}
